/**
 * ChartPage：命盘主界面
 * 只展示两类信息：
 * 1) 命盘（GridBoard：12 宫格 + 中宫）
 * 2) 大限/流年/流月/流日/流时栏（DaxianTable）
 * 命例录入/管理已迁移至 CasePage（左上角「命例」入口）
 */
import { useMemo } from 'react';
import { DaxianTable, GridBoard, type LayerPacks } from '@shared/render';
import { useChartStore } from '../state/store';

/** 十天干 */
const GAN = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
/** 五鼠遁偏移：日干 → 子时天干索引 */
const WUZHI_OFFSET: Record<string, number> = {
  甲: 0, 乙: 2, 丙: 4, 丁: 6, 戊: 8,
  己: 0, 庚: 2, 辛: 4, 壬: 6, 癸: 8,
};
/** 十二时辰标签 */
const SHICHEN_LABELS = ['子时','丑时','寅时','卯时','辰时','巳时','午时','未时','申时','酉时','戌时','亥时'];

/** 根据日干计算 12 时辰干支 */
function resolveHsiHours(dayGan: string): { label: string; gan: string; zhi: string }[] {
  const startIdx = WUZHI_OFFSET[dayGan];
  if (startIdx === undefined) return [];
  return SHICHEN_LABELS.map((label, i) => ({
    label,
    gan: GAN[(startIdx + i) % 10],
    zhi: ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'][i],
  }));
}

/** 组装当前视角的 LayerPacks（liuyue 层取当前选中月；流日盘取单元素） */
function useViewPacks(): LayerPacks {
  const layer = useChartStore((s) => s.layer);
  const packs = useChartStore((s) => s.packs);
  const liuyueMonth = useChartStore((s) => s.liuyueMonth);

  return useMemo(() => {
    const p: LayerPacks = { daxian: packs.daxian, liunian: packs.liunian, liuri: packs.liuri, liushi: packs.liushi };
    if (layer === 'liuri' && packs.liuriMonth) {
      // 流日盘：当月流月（单元素）
      p.liuyue = packs.liuriMonth;
    } else if (packs.liuyue) {
      const months = packs.liuyue.months;
      // 按 lunarMonth（负数=闰月）查找选中月；未选默认当前月
      const found =
        liuyueMonth === ''
          ? undefined
          : months.find((m) => m.lunarMonth === liuyueMonth);
      p.liuyue = found ?? months[liuyueMonth === '' ? new Date().getMonth() : 0] ?? months[0];
    }
    return p;
  }, [layer, packs, liuyueMonth]);
}

export default function ChartPage() {
  const base = useChartStore((s) => s.base);
  const layer = useChartStore((s) => s.layer);
  const activePalaceZhi = useChartStore((s) => s.activePalaceZhi);
  const sanfangZhi = useChartStore((s) => s.sanfangZhi);
  const clickPalace = useChartStore((s) => s.clickPalace);
  const error = useChartStore((s) => s.error);
  /** 当前命例（中宫姓名/钟表时间显示：name+input 已入库，前端透传） */
  const cases = useChartStore((s) => s.cases);
  const selectedCaseId = useChartStore((s) => s.selectedCaseId);
  /** 紫占不入库时以 transientCase 兜底中宫显示 */
  const transientCase = useChartStore((s) => s.transientCase);
  const currentCase = cases.find((c) => c.chartId === selectedCaseId) ?? transientCase;
  const selectDaxian = useChartStore((s) => s.selectDaxian);
  const selectLiunian = useChartStore((s) => s.selectLiunian);
  const selectLiuyueMonth = useChartStore((s) => s.selectLiuyueMonth);
  const selectLiuriDay = useChartStore((s) => s.selectLiuriDay);
  const selectLiushi = useChartStore((s) => s.selectLiushi);
  const clearDaxian = useChartStore((s) => s.clearDaxian);
  const clearLiunian = useChartStore((s) => s.clearLiunian);
  const clearLiuyue = useChartStore((s) => s.clearLiuyue);
  const clearLiuri = useChartStore((s) => s.clearLiuri);
  const clearLiushi = useChartStore((s) => s.clearLiushi);
  const daxianOrder = useChartStore((s) => s.daxianOrder);
  const year = useChartStore((s) => s.year);
  const liuyueMonth = useChartStore((s) => s.liuyueMonth);
  const liuriDate = useChartStore((s) => s.liuriDate);
  const shiIndex = useChartStore((s) => s.shiIndex);
  const packs = useChartStore((s) => s.packs);
  const viewPacks = useViewPacks();

  /** 点击大限列：已选中则取消查看（回本命），否则选中并联动 */
  function onToggleDaxian(order: number) {
    if (order === daxianOrder) clearDaxian();
    else void selectDaxian(order);
  }

  /** 点击流年：已选中则取消（回大限），否则选中 */
  function onToggleYear(y: number) {
    if (y === year) clearLiunian();
    else void selectLiunian(y);
  }

  /** 点击流月：已选中则取消（回流年），否则选中 */
  function onToggleMonth(m: number) {
    if (m === liuyueMonth) clearLiuyue();
    else void selectLiuyueMonth(m);
  }

  /** 点击流日：已选中则取消（回流月），否则选中 */
  function onToggleDay(d: { lunarYear: number; lunarMonth: number; lunarDay: number }) {
    const cur =
      layer === 'liuri' &&
      liuriDate.lunarYear === d.lunarYear &&
      liuriDate.lunarMonth === d.lunarMonth &&
      liuriDate.lunarDay === d.lunarDay;
    if (cur) clearLiuri();
    else void selectLiuriDay(d);
  }

  /** 点击流时：已选中则取消（回流日），否则选中 */
  function onToggleShi(shi: number) {
    if (shi === shiIndex) clearLiushi();
    else void selectLiushi(shi);
  }

  /** 当前选中流日（lunarMonth 负数 = 闰月；未选为 null） */
  const activeDay =
    liuriDate.lunarYear !== '' && liuriDate.lunarMonth !== '' && liuriDate.lunarDay !== ''
      ? { lunarMonth: liuriDate.lunarMonth, lunarDay: liuriDate.lunarDay }
      : null;

  /** 流时 12 时辰干支 */
  const liushiHours = useMemo(() => {
    if (!activeDay || !packs.liuriDays) return [];
    const dayInfo = packs.liuriDays.days.find(
      (d) => d.lunarMonth === activeDay!.lunarMonth && d.lunarDay === activeDay!.lunarDay,
    );
    if (!dayInfo) return [];
    return resolveHsiHours(dayInfo.gan);
  }, [activeDay, packs.liuriDays]);

  return (
    <div className="chart-page-fill flex flex-col gap-0.5">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
      )}

      {base ? (
        <>
          <GridBoard base={base} layer={layer} packs={viewPacks} name={currentCase?.name} input={currentCase?.input} hideBirth={currentCase?.hideBirth} activePalaceZhi={activePalaceZhi} sanfangZhi={sanfangZhi} onClickPalace={clickPalace} />
          <DaxianTable
            base={base}
            activeOrder={daxianOrder}
            activeYear={year}
            activeMonth={liuyueMonth}
            liuyueMonths={packs.liuyue?.months}
            liuriDays={packs.liuriDays?.days}
            liuriLunarYear={packs.liuriDays?.lunarYear}
            activeDay={activeDay}
            liushiHours={liushiHours}
            activeShi={shiIndex}
            onSelectOrder={onToggleDaxian}
            onSelectYear={onToggleYear}
            onSelectMonth={onToggleMonth}
            onSelectDay={onToggleDay}
            onSelectShi={onToggleShi}
          />
        </>
      ) : (
        <div className="rounded-lg bg-white px-4 py-16 text-center text-sm text-slate-400 shadow">
          暂无命例，请先到「命例」页添加命例
        </div>
      )}
    </div>
  );
}
